import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, ArrowRight, ChevronRight } from "lucide-react";
import { Toaster } from "sonner";

import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import ProductCard from "../components/ProductCard";
import QuoteDialog from "../components/QuoteDialog";
import CartDrawer from "../components/CartDrawer";
import SEO from "../components/SEO";
import { useCatalog } from "../hooks/useCatalog";
import { CATEGORY_SEO } from "../data/seoPages";

const CategoryPage = ({ seo }) => {
    const [quoteOpen, setQuoteOpen] = useState(false);
    const { data: catalogData, isLoading } = useCatalog();

    const products = useMemo(
        () => (catalogData?.items || []).filter((p) => p.category === seo.label),
        [catalogData, seo]
    );

    const otherCategories = Object.values(CATEGORY_SEO).filter((c) => c.slug !== seo.slug);

    return (
        <div className="App bg-[#FBFBFD] text-neutral-900 antialiased overflow-x-hidden">
            <SEO
                title={seo.metaTitle}
                description={seo.metaDescription}
                url={`https://dentiprime.ma${seo.path}`}
            />
            <Navbar onQuoteClick={() => setQuoteOpen(true)} />

            <main className="pt-[68px]">
                <section className="relative py-16 md:py-24 border-b border-black/[0.06]">
                    <div className="max-w-[1320px] mx-auto px-6 md:px-10">
                        <nav aria-label="Fil d'Ariane" className="flex items-center gap-1.5 text-[13px] text-neutral-500">
                            <Link to="/" className="hover:text-[#0a84ff] transition-colors">
                                Accueil
                            </Link>
                            <ChevronRight className="w-3.5 h-3.5" />
                            <span className="text-neutral-900 font-medium">{seo.label}</span>
                        </nav>
                        <h1 className="mt-6 font-display text-4xl md:text-5xl lg:text-[56px] font-light tracking-tight text-neutral-900 leading-[1.05] max-w-3xl">
                            {seo.h1}
                        </h1>
                        <p className="mt-6 text-[16px] md:text-[17px] leading-relaxed text-neutral-500 max-w-2xl">
                            {seo.intro}
                        </p>
                    </div>
                </section>

                <section className="py-16 md:py-20">
                    <div className="max-w-[1320px] mx-auto px-6 md:px-10">
                        {isLoading && (
                            <div className="flex justify-center py-16">
                                <Loader2 className="w-8 h-8 animate-spin text-[#0a84ff]" />
                            </div>
                        )}
                        {!isLoading && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                                {products.map((p, i) => (
                                    <ProductCard key={p.id} product={p} index={i} />
                                ))}
                            </div>
                        )}
                        {!isLoading && products.length === 0 && (
                            <p className="text-center text-neutral-500 py-16">
                                Aucun produit dans cette catégorie pour le moment.
                            </p>
                        )}
                    </div>
                </section>

                <section className="pb-20">
                    <div className="max-w-[1320px] mx-auto px-6 md:px-10">
                        <div className="dp-eyebrow">Autres catégories</div>
                        <div className="mt-4 flex flex-wrap gap-x-8 gap-y-3">
                            {otherCategories.map((c) => (
                                <Link
                                    key={c.slug}
                                    to={c.path}
                                    className="inline-flex items-center gap-2 text-[15px] font-medium text-[#0a84ff] hover:underline"
                                >
                                    {c.label}
                                    <ArrowRight className="w-4 h-4" />
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>
            </main>

            <Footer />
            <QuoteDialog open={quoteOpen} onOpenChange={setQuoteOpen} defaultProduct="" />
            <CartDrawer />
            <Toaster richColors position="bottom-right" />
        </div>
    );
};

export default CategoryPage;
